<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../SettingsSubPageHeader.vue';
import InboxesAPI from '../../../../api/inboxes';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const embedLink = ref('');
const isLoading = ref(false);
const phoneMigration = ref(false);
const popupWindow = ref(null);
const pollingInterval = ref(null);
const isPollingConnection = ref(false);
const isConnected = ref(false);

const inboxId = route.params.inbox_id;
const POLLING_INTERVAL_MS = 8000;
const MAX_POLLING_ATTEMPTS = 60;

const loadEmbedLink = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getGupshupOnboardingEmbedLink(inboxId, {
      phone_migration: phoneMigration.value,
    });
    embedLink.value = response.data?.embed_link || response.data?.embedLink || '';
    if (!embedLink.value) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.ERROR_MESSAGE'));
    }
  } catch (error) {
    if (error?.response?.data?.gupshup_feature_disabled) {
      router.replace({
        name: 'settings_inbox_list',
        params: { accountId: route.params.accountId },
      });
      return;
    }
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.ERROR_MESSAGE')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadEmbedLink);

const stopConnectionPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
  isPollingConnection.value = false;
};

const onPhoneMigrationToggle = async () => {
  await loadEmbedLink();
};

const closeOnboardingPopup = () => {
  try {
    popupWindow.value?.close();
  } catch (_) {
    // Ignore close errors and try by window name.
  }

  try {
    const namedWindow = window.open('', 'gupshup_onboarding');
    namedWindow?.close();
  } catch (_) {
    // Ignore close errors from named window fallback.
  }
};

const pollConnectionStatus = () => {
  stopConnectionPolling();
  isPollingConnection.value = true;
  let attempts = 0;

  pollingInterval.value = setInterval(async () => {
    attempts += 1;
    if (attempts > MAX_POLLING_ATTEMPTS) {
      stopConnectionPolling();
      return;
    }

    try {
      const response = await InboxesAPI.getGupshupConnectionStatus(inboxId);
      if (response.data?.connected) {
        isConnected.value = true;
        stopConnectionPolling();
        closeOnboardingPopup();
        useAlert(
          t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.CONNECTED_SUCCESS_MESSAGE')
        );
      }
    } catch (_) {
      // Keep polling until max attempts is reached.
    }
  }, POLLING_INTERVAL_MS);
};

const openEmbedLink = () => {
  if (!embedLink.value) return;

  const width = 1024;
  const height = 800;
  const left = Math.max(0, (window.screen.width - width) / 2);
  const top = Math.max(0, (window.screen.height - height) / 2);

  popupWindow.value = window.open(
    embedLink.value,
    'gupshup_onboarding',
    // Keep opener reference because this flow depends on polling + explicit popup close.
    // We accept this tradeoff with a trusted third-party onboarding domain.
    `width=${width},height=${height},left=${left},top=${top}`
  );
  pollConnectionStatus();
};

const goToAddAgents = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: inboxId,
    },
  });
};

onBeforeUnmount(stopConnectionPolling);
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <div class="w-full">
      <PageHeader
        :header-title="t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.TITLE')"
        :header-content="t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.DESC')"
      />
    </div>

    <div class="mt-6">
      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label class="flex items-center gap-2">
          <input
            id="gupshupPhoneMigration"
            v-model="phoneMigration"
            type="checkbox"
            @change="onPhoneMigrationToggle"
          />
          <span>
            {{ t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.PHONE_MIGRATION.LABEL') }}
          </span>
        </label>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.PHONE_MIGRATION.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="embedLink" class="w-full mt-4">
        <div class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.EMBED_INSTRUCTIONS') }}
        </div>
      </div>

      <div v-else class="w-full">
        <div v-if="isLoading" class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.LOADING') }}
        </div>
        <div v-else class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.ERROR_MESSAGE') }}
        </div>
      </div>
    </div>

    <div v-if="isConnected" class="w-full mt-6">
      <NextButton
        :disabled="isLoading || isPollingConnection"
        solid
        blue
        type="button"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.CONTINUE_BUTTON')"
        @click="goToAddAgents"
      />
    </div>

    <div class="w-full mt-4" v-if="embedLink && !isConnected">
      <NextButton
        :is-loading="isPollingConnection"
        solid
        type="button"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.GUPSHUP.ONBOARDING.CONNECT_BUTTON')"
        @click="openEmbedLink"
      />
    </div>
  </div>
</template>

